import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Form, Input, InputNumber, Popconfirm } from 'antd';
import { CardMainLayout, Catcher, cardLayoutOptions } from '../..';
import { INonRecurringCharges, INonRecurringChargesItem, NotificationType } from '../../../interfaces';
import { addNotification, apiAddFormErrorMessages } from '../../../middleware';
import {
    deleteGeneralNonRecurringCharge,
    getGeneralNonRecurringChargesList,
    postGeneralNonRecurringCharge,
    putGeneralNonRecurringCharge,
} from '../../../middleware';
import { globalHexId } from '../../../instruments';
import Styles from '../../styles/m_itemCardStyles.less';

interface IFormItem extends INonRecurringChargesItem {
    remove?: (id: string | number, listId: string | number) => void;
}

function FormItem({ remove, ...props }: IFormItem) {
    const [form] = Form.useForm();
    const [canSave, setCanSave] = useState(false);
    const [initialData, setInitialData] = useState<INonRecurringChargesItem>();

    const isNew = typeof initialData?.id === 'string';

    const onFormChange = () => {
        const formValues = { ...form.getFieldsValue() };
        const initValues = { ...(initialData || {}) } as INonRecurringChargesItem;
        delete (formValues as any).id;
        delete (initValues as any).id;
        setCanSave(JSON.stringify(formValues) !== JSON.stringify(initValues));
    };

    const onSave = async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue();

            let response;

            if (!isNew) {
                response = await putGeneralNonRecurringCharge(`${initialData?.id}`, values);
            } else {
                response = await postGeneralNonRecurringCharge(values);
            }

            const { isError, resp } = response;

            if (isError) {
                return apiAddFormErrorMessages(resp || {}, form);
            }

            addNotification({
                type: NotificationType.SUCCESS,
                message: 'General Non Recurring cost has been saved successfully',
            });

            const monthlyCharges = resp as unknown as INonRecurringChargesItem;

            setInitialData({ id: monthlyCharges.id, ...values });
            setCanSave(false);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        setInitialData(props);
    }, []);

    useEffect(() => {
        form.setFieldsValue(props);
    }, [JSON.stringify(props)]);

    return (
        <Form
            form={form}
            name={`${props.name}${props.id}`}
            initialValues={props}
            layout={'vertical'}
            className={'formStyle'}
            onValuesChange={onFormChange}
        >
            <div className={Styles.mrcFormItemContainer}>
                {props.predefinedType ? (
                    <>
                        <div className={Styles.mrcFormItemContainerHeader}>
                            {props.predefinedType === 'cards' ? 'Type: Monthly Sims' : ''}
                        </div>
                        <Divider />
                    </>
                ) : null}
                <div>
                    <Form.Item name="name" label={'Name'} rules={[{ required: true, message: 'Required' }]}>
                        <Input className={Styles.mrcFormItemInput} />
                    </Form.Item>
                </div>
                <div className={Styles.mrcFormItemAmountContainer}>
                    <Form.Item
                        name="amountCents"
                        label={'Amount'}
                        rules={[{ required: true, message: 'Required' }]}
                        style={{ width: '100%' }}
                    >
                        <InputNumber
                            step={1}
                            min={0}
                            precision={2}
                            className={Styles.mrcFormItemInput}
                            addonBefore={'EUR'}
                        />
                    </Form.Item>
                    <div className={Styles.mrcFormItemBottomBar}>
                        <Button type={'primary'} disabled={!canSave} onClick={onSave}>
                            Save
                        </Button>
                        {remove && !props.predefinedType ? (
                            <Popconfirm
                                disabled={isNew}
                                placement="top"
                                title={'Are you sure you want to delete?'}
                                okText="Delete"
                                cancelText="Cancel"
                                onConfirm={() => remove(initialData?.id || '', props.id)}
                            >
                                {isNew ? (
                                    <Button danger onClick={() => remove(initialData?.id || '', props.id)}>
                                        Delete
                                    </Button>
                                ) : (
                                    <Button danger>Delete</Button>
                                )}
                            </Popconfirm>
                        ) : null}
                    </div>
                </div>
            </div>
        </Form>
    );
}

export const BillingGeneralNRC: React.FC = observer(() => {
    const [list, setList] = useState<INonRecurringCharges>([]);
    const mrcFormItem = useRef<HTMLDivElement>(null);

    const scrollBottom = () => {
        if (mrcFormItem.current) {
            const { scrollHeight } = mrcFormItem.current;
            mrcFormItem.current.scrollTop = scrollHeight;
        }
    };

    const addNewItem = () => {
        const id = globalHexId.getId();
        const initNewItemValue: INonRecurringChargesItem = {
            amountCents: 0,
            id,
            name: '',
        };
        setList((prev) => [...prev, initNewItemValue]);
    };

    const removeItem = async (id: string | number, listId: string | number) => {
        const isNew = typeof id === 'string';
        if (!isNew) {
            const { isError } = await deleteGeneralNonRecurringCharge(id);
            if (isError) {
                return addNotification({
                    type: NotificationType.ERROR,
                    message: 'An error has occurred while deleting',
                    isUsersError: true,
                });
            }
        }

        const filteredArr = list.filter((value) => value.id !== listId);
        setList([...filteredArr]);
        if (!isNew) {
            addNotification({
                type: NotificationType.SUCCESS,
                message: 'General Non Recurring cost has been deleted successfully',
                isUsersError: true,
            });
        }
    };

    useEffect(() => {
        (async () => {
            const data = await getGeneralNonRecurringChargesList();
            setList(data);
        })();
    }, []);

    useEffect(() => {
        scrollBottom();
    }, [list]);

    return (
        <Catcher>
            <article className={Styles.main}>
                <CardMainLayout
                    name={'General Non Recurring Charges'}
                    columnOption={cardLayoutOptions.mrcEditForm}
                    idSpinners={[]}
                >
                    <div className={Styles.mrcFormItem} ref={mrcFormItem}>
                        {list.map((field: INonRecurringChargesItem) => {
                            return (
                                <div key={field.id}>
                                    <FormItem {...field} remove={removeItem} />
                                </div>
                            );
                        })}
                    </div>
                    <Button type="dashed" onClick={addNewItem} className={Styles.mrcAddButton}>
                        Add New
                    </Button>
                </CardMainLayout>
            </article>
        </Catcher>
    );
});
